import React from 'react'
import { Form, Input, Select, Spin, Divider, Row, Col, Space, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMedkit, faSearch, faIdBadge } from '@fortawesome/free-solid-svg-icons'
import AgiliteSkeleton from '../../../../reusable-components/AgiliteSkeleton'
import SectionWithBorder from './SectionWithBorder'

const { Text } = Typography

const MedicalAidForm = ({
  form,
  schemeLoading,
  schemeData,
  plansLoading,
  plansData,
  planOptionsLoading,
  planOptionData,
  debouncedSchemeFilter,
  handleGetMedicalAidPlans,
  handleGetMedicalAidPlanOptions,
  token
}) => {
  return (
    <SectionWithBorder title='Medical Aid Details' token={token} icon={faMedkit}>
      <Space direction='vertical' size={24} style={{ width: '100%' }}>
        {/* Medical Aid Scheme */}
        <Form.Item
          label={
            <Text strong style={{ fontSize: '15px' }}>
              Medical Aid Scheme
            </Text>
          }
          name={['medicalAid', 'name']}
        >
          <Select
            size='large'
            showSearch
            placeholder='Search for a Medical Aid Scheme'
            style={{ borderRadius: '8px' }}
            filterOption={false}
            onSearch={debouncedSchemeFilter}
            loading={schemeLoading}
            notFoundContent={schemeLoading ? <Spin size='small' /> : null}
            suffixIcon={<FontAwesomeIcon icon={faSearch} style={{ color: token.colorTextSecondary }} />}
            options={schemeData.map((scheme) => ({
              value: scheme.name,
              label: scheme.name,
              data: JSON.stringify(scheme)
            }))}
            onChange={(value, option) => {
              // Parse the scheme data
              const schemeData = option?.data ? JSON.parse(option.data) : null
              // Get current medical aid values
              const currentMedicalAid = form.getFieldValue('medicalAid') || {}

              // Update with new values
              const updatedMedicalAid = {
                ...currentMedicalAid,
                name: value,
                schemeCode: schemeData ? schemeData.code : '',
                // Reset dependent fields
                plan: undefined,
                planCode: undefined,
                planOption: undefined,
                planOptionCode: undefined
              }

              // Set all fields at once
              form.setFieldsValue({
                medicalAid: updatedMedicalAid
              })

              // Fetch plans if a scheme was selected
              if (schemeData) {
                handleGetMedicalAidPlans(schemeData.code)
              }
            }}
            allowClear
          />
        </Form.Item>

        {/* Medical Aid Plan */}
        <Form.Item
          label={
            <Text strong style={{ fontSize: '15px' }}>
              Medical Aid Plan
            </Text>
          }
          name={['medicalAid', 'plan']}
        >
          <Select
            size='large'
            showSearch
            placeholder='Select a Plan'
            style={{ borderRadius: '8px' }}
            filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
            loading={plansLoading}
            notFoundContent={plansLoading ? <Spin size='small' /> : null}
            options={plansData.map((plan) => ({
              value: plan.option,
              label: plan.option,
              data: JSON.stringify(plan)
            }))}
            onChange={(value, option) => {
              form.setFieldsValue({
                medicalAid: {
                  ...form.getFieldValue('medicalAid'),
                  planCode: option?.data ? JSON.parse(option.data).code : '',
                  planOption: undefined,
                  planOptionCode: undefined
                }
              })
              if (option?.data) {
                handleGetMedicalAidPlanOptions(JSON.parse(option.data).code)
              }
            }}
            disabled={!form.getFieldValue('medicalAid')?.schemeCode}
            allowClear
          />
        </Form.Item>

        {/* Plan Option */}
        <Form.Item
          label={
            <Text strong style={{ fontSize: '15px' }}>
              Plan Option
            </Text>
          }
          name={['medicalAid', 'planOption']}
        >
          <Select
            size='large'
            showSearch
            placeholder='Select a Plan Option'
            style={{ borderRadius: '8px' }}
            filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
            loading={planOptionsLoading}
            notFoundContent={planOptionsLoading ? <Spin size='small' /> : null}
            options={planOptionData.map((option) => ({
              value: option.option,
              label: option.option,
              data: JSON.stringify(option)
            }))}
            onChange={(value, option) => {
              form.setFieldsValue({
                medicalAid: {
                  ...form.getFieldValue('medicalAid'),
                  planOptionCode: option?.data ? JSON.parse(option.data).code : ''
                }
              })
            }}
            disabled={!form.getFieldValue('medicalAid')?.planCode}
            allowClear
          />
        </Form.Item>

        <Divider style={{ margin: '8px 0', borderColor: token.colorBorder }} />

        {/* Member Numbers */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Text strong style={{ fontSize: '15px' }}>
                  Medical Aid Number
                </Text>
              }
              name={['medicalAid', 'number']}
            >
              <Input
                size='large'
                placeholder='e.g. 123456789'
                style={{ borderRadius: '8px' }}
                prefix={
                  <FontAwesomeIcon icon={faIdBadge} style={{ color: token.colorTextSecondary, marginRight: '8px' }} />
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                <Text strong style={{ fontSize: '15px' }}>
                  Dependant Number
                </Text>
              }
              name={['medicalAid', 'dependantNumber']}
            >
              <Input
                size='large'
                placeholder='e.g. 01'
                style={{ borderRadius: '8px' }}
                prefix={
                  <FontAwesomeIcon icon={faIdBadge} style={{ color: token.colorTextSecondary, marginRight: '8px' }} />
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </Space>
    </SectionWithBorder>
  )
}

export default MedicalAidForm
